// One state machine drives ALL NINE detail write actions. The failure `code` gets a home so the
// view can render case-specific error copy, and the state is held above the skeleton its own write
// raises, so it survives the success refetch (panel mutation pattern).
// It is INSTANTIATED FOUR TIMES, because the actions live in four places on screen: DeliveryLayout
// runs one for the «Acciones» card's six rows (four for the «Usuario» audience, «Recuperado» and
// «Recuperación vacía» for the «Mensajero» one) — whose mutual exclusion falls out of the single
// `action` slot, since only one action can own that flow at a time — and one for «Ampliar custodia», which
// hangs off the «Expira la recogida» milestone in the Trazabilidad; LockCodeRails runs the third,
// for «Confirmar secuencia» on the open carril, and BoxMarksBanner the fourth, for clearing the
// «buzón»'s marks at the head of the «Ver log buzón» region. Separate instances rather than one slot: they are
// physically separate controls, and sharing the slot would blank the «Acciones» card while the agent
// picks a day in the timeline. The third instance serves BOTH carriles, since only one is ever open
// — which is why its owner has to clear it when the open one changes.
// `date` is the payload of the ONE parameterized action, «Ampliar custodia». A single slot rather
// than a second state machine: the same mutual exclusion that keeps two actions from owning the
// flow also guarantees at most one payload is live. The SET_DATE event carries `string`, never
// null — the calendar can only emit a valid day — so the null lives in the STATE alone, meaning
// «no action in flow, or an action that takes no payload».
// Two of them do not move a `Delivery` at all: `confirmSequence` moves a cerradura's puntero, and
// `clearBoxMarks` clears the «buzón»'s desync marks. Neither takes a payload — the sequence derives
// its two positions at submit, and the clear attests a resync that already happened physically.
export type DeliveryActionKind =
  | 'confirm'
  | 'restore'
  | 'nonCollect'
  | 'reject'
  | 'nonRetrieve'
  | 'retrieve'
  | 'extendCustody'
  | 'confirmSequence'
  | 'clearBoxMarks';
export type DeliveryActionPhase = 'idle' | 'confirming' | 'pending' | 'success' | 'error';

export interface DeliveryActionState {
  action: DeliveryActionKind | null; // which action owns the flow; null only when idle
  phase: DeliveryActionPhase;
  errorCode: string | null; // the ApiError.code captured on failure (restore, extendCustody)
  date: string | null; // `YYYY-MM-DD` payload of the parameterized action
}

export type DeliveryActionEvent =
  | { type: 'START'; action: DeliveryActionKind; date?: string }
  | { type: 'SET_DATE'; date: string }
  | { type: 'CANCEL' }
  | { type: 'SUBMIT' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; code: string | null }
  | { type: 'DISMISS' };

export const initialDeliveryActionState: DeliveryActionState = {
  action: null,
  phase: 'idle',
  errorCode: null,
  date: null,
};

// Guarded transitions; an event invalid for the current phase is a no-op (returns the state
// unchanged), so a stray dispatch can never land the card in an impossible state.
export function deliveryActionFlow(
  state: DeliveryActionState,
  event: DeliveryActionEvent,
): DeliveryActionState {
  switch (event.type) {
    case 'START':
      return state.phase === 'idle'
        ? { action: event.action, phase: 'confirming', errorCode: null, date: event.date ?? null }
        : state;
    // Guarded to `confirming` for the same reason SUBMIT is: once the write is in flight, a late
    // dispatch must not change the value that was already sent.
    case 'SET_DATE':
      return state.phase === 'confirming' ? { ...state, date: event.date } : state;
    case 'CANCEL':
      return state.phase === 'confirming' ? initialDeliveryActionState : state;
    case 'SUBMIT':
      return state.phase === 'confirming' ? { ...state, phase: 'pending' } : state;
    // SUCCESS and ERROR both PRESERVE `date` through `...state`: the success banner names the day
    // that was applied, and a failed attempt keeps the agent's choice for the retry.
    case 'SUCCESS':
      return state.phase === 'pending' ? { ...state, phase: 'success', errorCode: null } : state;
    case 'ERROR':
      return state.phase === 'pending' ? { ...state, phase: 'error', errorCode: event.code } : state;
    case 'DISMISS':
      return state.phase === 'success' || state.phase === 'error'
        ? initialDeliveryActionState
        : state;
    default:
      return state;
  }
}
